import React, { useEffect, useState } from 'react';
import { Box, Typography, Button, CircularProgress } from '@mui/material';
import { Print } from '@mui/icons-material';
import { useRouter } from '../contexts/RouterContext';
import { useAuth } from '../contexts/AuthContext';
import { printTemplateService, renderTemplateHtml, PrintTemplate } from '../services/printTemplateService';
import { PrintTemplateBar, usePrintTemplateChoice } from './print/PrintTemplateChoice';
import { colors } from '../theme/designSystem';
import { contractsService, ContractRow, CONTRACT_STATUS_LABELS } from '../services/contractsService';

/**
 * Impressão de um contrato (?page=contract-print&contractId=): o texto final gravado na emissão,
 * dentro do template da clínica (o escolhido na versão do modelo vem pré-selecionado; o padrão da
 * entidade senão), mesmo esquema das demais impressões (RN-408a). Contrato cancelado sai com a
 * tarja, para a cópia impressa não passar por válida.
 */
const formatDate = (iso: string | null) => (iso ? iso.slice(0, 10).split('-').reverse().join('/') : '');
const formatDateTime = (iso: string | null) => (iso ? new Date(iso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' }) : '');

const ContractPrint: React.FC = () => {
  const { getParam } = useRouter();
  const { user } = useAuth();
  const contractId = getParam('contractId') || '';

  const [contract, setContract] = useState<ContractRow | null>(null);
  const [templates, setTemplates] = useState<PrintTemplate[]>([]);
  const templateChoice = usePrintTemplateChoice(templates, 'contract');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [applied, setApplied] = useState(false);

  useEffect(() => {
    document.title = 'Clinic4Us - Impressão de contrato';
    const load = async () => {
      try {
        const [row, list] = await Promise.all([
          contractsService.getById(contractId),
          user?.entityId ? printTemplateService.getByEntity(user.entityId).catch(() => []) : Promise.resolve([]),
        ]);
        setContract(row);
        setTemplates(list);
      } catch {
        setError('Não foi possível carregar o contrato para impressão.');
      } finally {
        setLoading(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contractId]);

  // O template da versão do modelo manda uma vez; depois a pessoa troca à vontade na barra
  useEffect(() => {
    if (applied || !contract?.printTemplateId || templateChoice.templates.length === 0) return;
    if (templateChoice.templates.some((t) => t.id === contract.printTemplateId)) {
      templateChoice.setTemplateId(contract.printTemplateId);
    }
    setApplied(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contract, templateChoice.templates, applied]);

  const template = templateChoice.template;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress sx={{ color: colors.primary }} />
      </Box>
    );
  }

  if (error || !contract) {
    return (
      <Typography sx={{ color: colors.error, textAlign: 'center', py: 8 }}>{error || 'Contrato não encontrado.'}</Typography>
    );
  }

  const cancelled = contract.status === 'cancelled';

  // Rodapé de toda folha: identificação do contrato + rodapé da clínica. No papel ele é fixo no pé de cada
  // folha e o tfoot reserva a altura, para o texto não correr por baixo (mesmo esquema das evoluções).
  const footer = (
    <>
      <Box sx={{ fontSize: '8pt', color: '#495057', lineHeight: 1.5, textAlign: 'center', borderTop: '1px solid #ddd', pt: '2mm' }}>
        <div><strong>Emitido por:</strong> {contract.issuedByEmail || contract.issuedByName || '—'} em {formatDateTime(contract.issuedAt)}</div>
        <div>
          <strong>Contrato:</strong> {contract.templateTitle} · versão {contract.templateVersion} · {CONTRACT_STATUS_LABELS[contract.status]}
          {' · '}<strong>Id:</strong> <span style={{ fontFamily: 'monospace' }}>{user?.alias ? `${user.alias}/` : ''}{contract.id}</span>
        </div>
      </Box>
      {template && (
        <div
          style={{ textAlign: 'center', fontSize: '8.5pt', lineHeight: 1.4, marginTop: '2mm', paddingTop: '2mm', borderTop: '1px solid #ddd' }}
          dangerouslySetInnerHTML={{ __html: renderTemplateHtml(template.footer) }}
        />
      )}
    </>
  );

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#f5f5f5', padding: '2rem', '@media print': { padding: 0, backgroundColor: '#fff' } }}>
      <style>{`
        @media print {
          /* Margem da folha zerada: sem ela o navegador não tem onde imprimir data, título da aba, URL e
             número de página. As margens viram espaço dentro do conteúdo: laterais no padding da página,
             topo no thead (repete em toda folha) e pé no tfoot reservado + rodapé fixo. */
          @page { size: A4 portrait; margin: 0; }
          .contract-print-page { padding: 0 15mm !important; width: auto !important; min-height: 0 !important; box-shadow: none !important; margin: 0 !important; }
          .contract-frame { width: 100%; border-collapse: collapse; }
          .contract-frame > thead { display: table-header-group; }
          .contract-frame > tfoot { display: table-footer-group; }
          .contract-frame > thead > tr > td,
          .contract-frame > tbody > tr > td,
          .contract-frame > tfoot > tr > td { padding: 0; }
          .contract-frame-top { display: block !important; height: 12mm; }
          .contract-frame-footer-space { display: block !important; height: 40mm; }
          .contract-frame-footer { position: fixed !important; left: 15mm !important; right: 15mm !important; bottom: 8mm !important; }
          .print-frame-header img { max-height: 18mm !important; }
        }
      `}</style>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5, flexWrap: 'wrap', mb: 2, maxWidth: '210mm', mx: 'auto', '@media print': { display: 'none' } }}>
        <PrintTemplateBar choice={templateChoice} />
        <Button variant="contained" startIcon={<Print />} onClick={() => window.print()} sx={{ backgroundColor: colors.primary, textTransform: 'none', '&:hover': { backgroundColor: colors.primaryHover } }}>
          Imprimir
        </Button>
      </Box>

      <Box
        className="contract-print-page"
        sx={{
          width: '210mm', maxWidth: '100%', minHeight: '297mm', padding: '15mm', paddingBottom: '48mm', boxSizing: 'border-box',
          background: 'white', margin: '0 auto 2rem', position: 'relative', boxShadow: '0 0 8px rgba(0,0,0,0.15)', fontSize: '12pt', color: '#000',
        }}
      >
        <table className="contract-frame" style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
          <thead><tr><td>
            <div className="contract-frame-top" style={{ display: 'none' }} />
            {template && (
              <div className="print-frame-header">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '10mm' }}>
                  <div style={{ flexShrink: 0 }}>
                    {template.file ? <img src={template.file} alt="logo" style={{ maxHeight: '30mm', maxWidth: '70mm', objectFit: 'contain' }} /> : null}
                  </div>
                  <div style={{ textAlign: 'right', fontSize: '10pt', lineHeight: 1.5 }} dangerouslySetInnerHTML={{ __html: renderTemplateHtml(template.header) }} />
                </div>
                <hr style={{ border: 'none', borderTop: '1px solid #999', margin: '6mm 0' }} />
              </div>
            )}
          </td></tr></thead>

          <tfoot><tr><td>
            <div className="contract-frame-footer-space" style={{ display: 'none' }} />
          </td></tr></tfoot>

          <tbody><tr><td>
            {cancelled && (
              <Box sx={{ border: '2px solid #c82333', color: '#c82333', textAlign: 'center', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', py: '2mm', mb: '6mm', fontSize: '11pt' }}>
                Contrato cancelado em {formatDate(contract.cancelledAt)}{contract.cancelReason ? ` — ${contract.cancelReason}` : ''}
              </Box>
            )}
            {/* Sem título automático: o texto do modelo traz o seu (como nos documentos, RN-537s) */}
            <Box
              sx={{ fontSize: '12pt', color: '#000', lineHeight: 1.6, '& p': { m: 0, mb: '2mm' }, '& ul, & ol': { my: '2mm', pl: '8mm' } }}
              dangerouslySetInnerHTML={{ __html: contract.content }}
            />
          </td></tr></tbody>
        </table>

        {/* Na tela: no pé da página de prévia. No papel: fixo no pé de cada folha */}
        <div className="contract-frame-footer" style={{ position: 'absolute', left: '15mm', right: '15mm', bottom: '10mm' }}>
          {footer}
        </div>
      </Box>
    </Box>
  );
};

export default ContractPrint;
